import {
    nextTick,
    onBeforeUnmount,
    readonly,
    ref,
    toValue,
    watch,
    type MaybeRefOrGetter,
} from 'vue';

export const useTextTruncation = (
    target: MaybeRefOrGetter<HTMLElement | null>,
) => {
    const isTruncated = ref<boolean>(false);

    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const disconnectResizeObserver = () => {
        if (!resizeObserver) {
            return;
        }

        resizeObserver.disconnect();
        resizeObserver = null;
    };

    const disconnectMutationObserver = () => {
        if (!mutationObserver) {
            return;
        }

        mutationObserver.disconnect();
        mutationObserver = null;
    };

    const stopObserving = () => {
        disconnectResizeObserver();
        disconnectMutationObserver();
    };

    const compute = (element: HTMLElement | null) => {
        if (!element) {
            isTruncated.value = false;
            return;
        }

        isTruncated.value = element.scrollWidth > element.clientWidth;
    };

    const observe = (element: HTMLElement) => {
        stopObserving();

        resizeObserver = new ResizeObserver(() => {
            compute(element);
        });
        resizeObserver.observe(element);

        mutationObserver = new MutationObserver(() => {
            compute(element);
        });
        mutationObserver.observe(
            element,
            {
                attributes: true,
                characterData: true,
                childList: true,
                subtree: true,
            },
        );
    };

    watch(
        () => toValue(target),
        async (element) => {
            if (!element) {
                stopObserving();
                isTruncated.value = false;
                return;
            }

            await nextTick();
            compute(element);
            observe(element);
        },
        { immediate: true },
    );

    onBeforeUnmount(() => {
        stopObserving();
    });

    return {
        isTruncated: readonly(isTruncated),
    };
};
